export const SESSION_FAILURE_UNAUTHENTICATED = "unauthenticated";
export const SESSION_FAILURE_TRANSIENT = "transient";

/** Initial attempt plus these three backoff delays = 4 tries total. */
export const SESSION_RESTORE_RETRY_DELAYS_MS = [500, 1500, 3500];

export function isAbortError(error) {
  return error?.name === "AbortError";
}

/**
 * Classify a failed GET /auth/session outcome.
 *
 * @param {{ status?: number, error?: unknown }} [input]
 * @returns {"unauthenticated" | "transient"}
 */
export function classifySessionFailure(input = {}) {
  const status = input.status;

  if (status === 401 || status === 403) {
    return SESSION_FAILURE_UNAUTHENTICATED;
  }

  return SESSION_FAILURE_TRANSIENT;
}

export function waitMs(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error("Aborted");
      error.name = "AbortError";
      reject(error);
      return;
    }

    const timer = setTimeout(() => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    }, ms);

    function onAbort() {
      clearTimeout(timer);
      const error = new Error("Aborted");
      error.name = "AbortError";
      reject(error);
    }

    signal?.addEventListener?.("abort", onAbort, { once: true });
  });
}

async function readJsonBody(response) {
  try {
    return { body: await response.json() };
  } catch (error) {
    return { error };
  }
}

/**
 * Fetch /auth/session with bounded retries on transient failures.
 *
 * @returns {Promise<
 *   | { type: "success", user: object }
 *   | { type: "unauthenticated" }
 *   | { type: "transient" }
 *   | { type: "aborted" }
 * >}
 */
export async function restoreSessionWithRetry({
  url,
  fetchOptions,
  fetchFn = fetch,
  waitFn = waitMs,
  retryDelaysMs = SESSION_RESTORE_RETRY_DELAYS_MS,
  isCancelled,
} = {}) {
  const maxAttempts = retryDelaysMs.length + 1;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isCancelled?.() || fetchOptions?.signal?.aborted) {
      return { type: "aborted" };
    }

    let outcome;
    try {
      const response = await fetchFn(url, fetchOptions);
      if (isCancelled?.() || fetchOptions?.signal?.aborted) {
        return { type: "aborted" };
      }

      const parsed = await readJsonBody(response);
      if (parsed.error) {
        outcome = {
          kind: classifySessionFailure({
            status: response.status,
            error: parsed.error,
          }),
        };
      } else if (response.ok && parsed.body?.user) {
        return { type: "success", user: parsed.body.user };
      } else if (response.ok && !parsed.body?.user) {
        outcome = { kind: SESSION_FAILURE_UNAUTHENTICATED };
      } else {
        outcome = {
          kind: classifySessionFailure({ status: response.status }),
        };
      }
    } catch (error) {
      if (isAbortError(error) || isCancelled?.()) {
        return { type: "aborted" };
      }
      outcome = { kind: classifySessionFailure({ error }) };
    }

    if (outcome.kind === SESSION_FAILURE_UNAUTHENTICATED) {
      return { type: "unauthenticated" };
    }

    const delayMs = retryDelaysMs[attempt];
    if (delayMs == null) {
      break;
    }

    if (isCancelled?.() || fetchOptions?.signal?.aborted) {
      return { type: "aborted" };
    }

    try {
      await waitFn(delayMs, fetchOptions?.signal);
    } catch (error) {
      if (isAbortError(error) || isCancelled?.()) {
        return { type: "aborted" };
      }
      return { type: "transient" };
    }
  }

  return { type: "transient" };
}
